import React, { useMemo, useState } from 'react';
import { ScrollView, StyleSheet, TouchableOpacity, View } from 'react-native';
import { Button, Text } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import { getTags } from '../utils';
import { JobType } from '../types';
import { Palette, radius, spacing, useThemePalette } from '../theme';
import DatePicker from './DatePicker';
import ModalSheet from './ModalSheet';

interface FilterModalProps {
  visible: boolean;
  onDismiss: () => void;
  pickedTags: string[];
  pickedDate: string;
  showAppliedOnly: boolean;
  jobs: JobType[];
  setPickedTags: (tags: string[]) => void;
  setShowAppliedOnly: (value: boolean) => void;
  clearFilters: () => void;
  setPickedDate: (date: string) => void;
  hasActiveFilters: boolean;
  clearFavorites?: () => void;
  appliedIds: string[];
}

const FilterModal: React.FC<FilterModalProps> = ({
  visible,
  onDismiss,
  pickedTags,
  pickedDate,
  showAppliedOnly,
  setPickedTags,
  setShowAppliedOnly,
  setPickedDate,
  clearFilters,
  hasActiveFilters,
  jobs,
  clearFavorites,
  appliedIds,
}) => {
  const [showCalendar, setShowCalendar] = useState(false);
  const { t } = useTranslation();
  const palette = useThemePalette();
  const styles = useMemo(() => makeStyles(palette), [palette]);

  const handleDateConfirm = (selectedDate: Date) => {
    setPickedDate(selectedDate.toJSON());
    setShowCalendar(false);
  };

  const formattedDate = new Date(pickedDate).toUTCString().slice(5, 16);
  const allTags = getTags(jobs);
  const filteredJobs = jobs.filter(
    (job) =>
      new Date(job.date) >= new Date(pickedDate) &&
      (!showAppliedOnly || appliedIds.includes(job.id)) &&
      (pickedTags.length === 0 || job.tags?.some((tag) => pickedTags.includes(tag)))
  );

  const toggleTag = (tag: string) => {
    if (pickedTags.includes(tag)) {
      setPickedTags(pickedTags.filter((x) => x !== tag));
    } else {
      setPickedTags([...pickedTags, tag]);
    }
  };

  return (
    <ModalSheet
      visible={visible}
      onDismiss={onDismiss}
      cardStyle={styles.card}
    >
      <View style={styles.body}>
        <View style={styles.header}>
          <Text style={styles.title}>
            {t('filter.title')}{' '}
            <Text style={styles.titleCount}>
              ({filteredJobs.length} {t('filter.total')})
            </Text>
          </Text>
          <TouchableOpacity
            onPress={clearFilters}
            disabled={!hasActiveFilters}
            hitSlop={8}
            accessibilityState={{ disabled: !hasActiveFilters }}
          >
            <Text
              style={[styles.reset, !hasActiveFilters && styles.resetDisabled]}
            >
              {t('filter.reset')}
            </Text>
          </TouchableOpacity>
        </View>

        <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent}>
          <Text style={styles.sectionTitle}>{t('filter.byDate')}</Text>
          <TouchableOpacity
            style={styles.dateChip}
            onPress={() => setShowCalendar(true)}
            activeOpacity={0.85}
          >
            <Text style={styles.dateChipText}>
              {t('filter.postedAfter')}: {formattedDate}
            </Text>
          </TouchableOpacity>

          <DatePicker
            isVisible={showCalendar}
            onCancel={() => setShowCalendar(false)}
            onConfirm={handleDateConfirm}
            initialDate={new Date(pickedDate)}
            maximumDate={new Date()}
          />

          <Text style={[styles.sectionTitle, { marginTop: spacing.lg }]}> {t('filter.byApplied')}</Text>
          <TouchableOpacity
            style={[styles.toggleChip, showAppliedOnly && styles.toggleChipSelected]}
            onPress={() => setShowAppliedOnly(!showAppliedOnly)}
            activeOpacity={0.85}
          >
            <Text style={[styles.toggleChipText, showAppliedOnly && styles.toggleChipTextSelected]}>
              {showAppliedOnly ? t('filter.appliedOnlyOn') : t('filter.appliedOnlyOff')}
            </Text>
          </TouchableOpacity>

          <Text style={[styles.sectionTitle, { marginTop: spacing.lg }]}>
            {t('filter.byTag')}
          </Text>
          <View style={styles.tagsWrap}>
            {allTags.map((tag) => {
              const selected = pickedTags.includes(tag);
              return (
                <TouchableOpacity
                  key={tag}
                  onPress={() => toggleTag(tag)}
                  style={[styles.tagChip, selected && styles.tagChipSelected]}
                  activeOpacity={0.85}
                >
                  <Text
                    style={[
                      styles.tagChipText,
                      selected && styles.tagChipTextSelected,
                    ]}
                  >
                    {tag}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>

          {clearFavorites && (
            <Button
              mode="contained"
              buttonColor={palette.danger}
              icon="delete"
              onPress={clearFavorites}
              style={{ marginTop: spacing.lg }}
            >
              {t('filter.clearAllFavorites')}
            </Button>
          )}
        </ScrollView>

        <View style={styles.footer}>
          <Button
            mode="contained"
            onPress={onDismiss}
            buttonColor={palette.primary}
            textColor="#0F1115"
            style={styles.footerBtn}
          >
            {t('common.ok')}
          </Button>
        </View>
      </View>
    </ModalSheet>
  );
};

const makeStyles = (p: Palette) =>
  StyleSheet.create({
    card: {
      backgroundColor: p.surface,
      borderRadius: radius.lg,
    },
    body: { padding: spacing.lg },
    header: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: spacing.md,
    },
    title: { fontSize: 20, fontWeight: '700', color: p.textPrimary },
    titleCount: { fontSize: 14, color: p.textMuted, fontWeight: '400' },
    reset: { color: p.appliedText, fontSize: 14, fontWeight: '600' },
    resetDisabled: { color: p.textMuted, opacity: 0.5 },
    scroll: { maxHeight: 480 },
    scrollContent: { paddingBottom: spacing.md },
    sectionTitle: {
      fontSize: 15,
      fontWeight: '700',
      color: p.textPrimary,
      marginBottom: spacing.sm,
    },
    dateChip: {
      alignSelf: 'flex-start',
      backgroundColor: p.appliedBg,
      paddingHorizontal: spacing.md,
      paddingVertical: spacing.sm,
      borderRadius: radius.pill,
    },
    dateChipText: { color: p.appliedText, fontWeight: '600', fontSize: 13 },
    tagsWrap: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm },
    tagChip: {
      paddingHorizontal: spacing.md,
      paddingVertical: spacing.sm,
      borderRadius: radius.pill,
      backgroundColor: p.surfaceMuted,
      borderWidth: 1,
      borderColor: p.border,
    },
    tagChipSelected: {
      backgroundColor: p.primary,
      borderColor: p.primary,
    },
    tagChipText: { fontSize: 12, color: p.textPrimary },
    tagChipTextSelected: { color: '#0F1115', fontWeight: '700' },
    toggleChip: {
      alignSelf: 'flex-start',
      backgroundColor: p.surfaceMuted,
      paddingHorizontal: spacing.md,
      paddingVertical: spacing.sm,
      borderRadius: radius.pill,
      borderWidth: 1,
      borderColor: p.border,
    },
    toggleChipSelected: {
      backgroundColor: p.appliedBg,
      borderColor: p.appliedBg,
    },
    toggleChipText: { color: p.textPrimary, fontSize: 13, fontWeight: '600' },
    toggleChipTextSelected: { color: p.appliedText },
    footer: {
      flexDirection: 'row',
      justifyContent: 'flex-end',
      gap: spacing.sm,
      marginTop: spacing.md,
    },
    footerBtn: { borderRadius: radius.pill },
  });

export default FilterModal;
